import React, { useEffect, useState } from "react";
import {
  Package,
  Calendar,
  RotateCcw,
  Clock3,
  Star,
} from "lucide-react";
import CustomerOrderDetails from "./CustomerOrderDetails";
import ReviewModal from "../../../components/customer/ReviewModal";
import {
  useLazyGetCustomerOrdersQuery,
  useRateOrderMutation,
} from "../../../redux/actions/Customer/cartCustomer";
import { ImagePath } from "../../../assests";
import { useUploadCustomerFileMutation } from "../../../redux/actions/Customer/profileCustomer";

export const getCustomerStatusBadge = (status: number) => {
  switch (status) {
    case 1:
      return {
        text: "Pending Review",
        styles: "bg-amber-50 text-amber-700 border-amber-200",
      };
    case 2:
      return {
        text: "Payment Pending",
        styles: "bg-orange-50 text-orange-700 border-orange-200",
      };
    case 3:
      return {
        text: "Order Confirmed",
        styles: "bg-blue-50 text-blue-700 border-blue-200",
      };
    case 4:
      return {
        text: "Processing",
        styles: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    case 5:
      return {
        text: "Dispatched",
        styles: "bg-purple-50 text-purple-700 border-purple-200",
      };
    case 6:
      return {
        text: "Delivered",
        styles: "bg-green-50 text-green-700 border-green-200",
      };
    case 7:
      return {
        text: "Cancelled",
        styles: "bg-rose-50 text-rose-700 border-rose-200",
      };
    case 8:
      return {
        text: "Refunded to Wallet",
        styles: "bg-slate-100 text-slate-700 border-slate-200",
      };
    default:
      return {
        text: "Placed",
        styles: "bg-slate-50 text-slate-600 border-slate-200",
      };
  }
};

interface Props {
  selectedOrderId?: number;
}

const CustomerOrdersScreen: React.FC<Props> = ({ selectedOrderId }) => {
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedReviewOrder, setSelectedReviewOrder] = useState<any>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState<"create" | "view" | "edit" | null>(null);

  const [orderReviews, setOrderReviews] = useState<
    Record<number, { rating: number; comment: string; images: string[] }>
  >({});

  const [
    getCustomerOrders,
    { data: customerOrders, isLoading },
  ] = useLazyGetCustomerOrdersQuery();

  const [rateOrder] = useRateOrderMutation();
  const [uploadCustomerFile] = useUploadCustomerFileMutation();

  const orders = customerOrders?.orders ?? [];

  useEffect(() => {
    if (!selectedOrderId) return;
    if (orders.length === 0) return;

    const order = orders.find((x) => x.orderId === selectedOrderId);
    if (order) {
      setSelectedOrder(order);
    }
  }, [selectedOrderId, orders]);

  useEffect(() => {
    getCustomerOrders({
      Search: "",
      Status: "",
      Page: 1,
      PageSize: 10,
    });
  }, []);

  const handleOpenOrderReview = (e: React.MouseEvent, order: any, mode: "create" | "view" | "edit") => {
    e.stopPropagation();
    setSelectedReviewOrder(order);
    setReviewMode(mode);
    setIsReviewModalOpen(true);
  };

  const handleSubmitOrderReview = async (rating: number, comment: string, images: (File | string)[]) => {
    if (!selectedReviewOrder) return;

    try {
      const uploadedUrls: string[] = [];
      for (const item of images) {
        if (item instanceof File) {
          const response = await uploadCustomerFile({
            File: item,
            FolderName: "Reviews/Orders",
          }).unwrap();
          
          if (response?.fileUrl) {
            uploadedUrls.push(response.fileUrl);
          }
        } else if (typeof item === "string") {
          uploadedUrls.push(item);
        }
      }

      await rateOrder({
        OrderId: selectedReviewOrder.orderId,
        data: {
          rating,
          comment,
          images: uploadedUrls,
        },
      }).unwrap();

      setOrderReviews((prev) => ({
        ...prev,
        [selectedReviewOrder.orderId]: {
          rating,
          comment,
          images: uploadedUrls,
        },
      }));
    } catch (error) {
      console.error("Failed to submit order review:", error);
    } finally {
      setIsReviewModalOpen(false);
      setSelectedReviewOrder(null);
      setReviewMode(null);
    }
  };
    const handleRatingChange = (rating: number) => {
    if (rating >= 4) {
      window.open(
        "https://search.google.com/local/writereview?placeid=ChIJWTRemTliXz4Rbqad9gAaJc0",
        "_blank",
        "noopener,noreferrer"
      );

      // Close our popup
      setIsReviewModalOpen(false);
      setSelectedReviewOrder(null);
      setReviewMode(null);

      return;
    }

    // 1, 2, 3
    // Do nothing here.
    //
    // Popup stays open.
    // User can enter comment.
    // User can upload images.
    // User clicks Update Review.
    // Then handleSubmitOrderReview() will call API.
  };


  if (selectedOrder) {
    return (
      <CustomerOrderDetails
        order={selectedOrder}
        onBack={() => setSelectedOrder(null)}
      />
    );
  }

  return (
    <div className="space-y-4 text-left w-full p-4 sm:p-6 bg-white min-h-screen rounded-2xl">
      <div className="border-b border-slate-200 pb-3">
        <h2 className="text-xl font-bold text-[#2F5E2E] tracking-tight flex items-center gap-2">
          <Package className="w-5 h-5 text-[#6DBE45]" />
          My Purchase History
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          View orders and track deliveries.
        </p>
      </div>

      {!isLoading && orders.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center text-xs text-slate-500 font-medium shadow-sm">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          You haven't placed any orders yet. Once you buy something, it will
          appear right here!
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 w-full">
          {orders.map((order) => {
            const isDelivered =
              order.displayStatus?.toLowerCase() === "delivered" 

            const statusBadge = {
              text: order.displayStatus,
              styles:
                order.displayStatus === "Confirmed"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : order.displayStatus === "Cancelled"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : order.displayStatus === "Processing"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-slate-50 text-slate-700 border-slate-200",
            };

            const allItems = [
              {
                id: order.orderId,
                productName: order.primaryItemName,
                variantName: order.primaryItemType,
                image: order.thumbnailImage || ImagePath.NoImage,
                quantity: 1,
                price: (order as any).primaryItemPrice ?? null,
              },
            ];

            const visibleItems = allItems.filter(
              (item) => item.price === null || item.price > 0
            );

            const totalItems = order.totalItems;
            const existingReview = orderReviews[order.orderId];

            return (
              <div
                key={order.orderId}
                onClick={() => setSelectedOrder(order)}
                className="group cursor-pointer select-none rounded-xl border border-slate-200 bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#6DBE45] hover:shadow-lg hover:shadow-[#6DBE45]/5 w-full"
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1.5">
                          Order:
                        </span>
                        <span className="text-sm font-semibold text-slate-800">
                          {order.orderNumber}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar size={13} />
                          {new Date(order.orderDate).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          <Clock3 size={13} />
                          {new Date(order.orderDate).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>

                        <div>
                          <span className="font-semibold text-slate-700">
                            {totalItems}
                          </span>{" "}
                          Item{totalItems > 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${statusBadge.styles}`}
                    >
                      {statusBadge.text}
                    </span>
                  </div>

                  {/* Products Overview */}
                  <div className="space-y-2">
                    {visibleItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start sm:items-center rounded-xl border border-slate-200 bg-slate-50/50 p-3 gap-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full flex-1">
                          <img
                            src={item.image}
                            alt={item.productName || "Product"}
                            className="h-14 w-14 rounded-lg border border-slate-200 bg-white object-cover shadow-sm shrink-0"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = ImagePath.NoImage;
                            }}
                          />

                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <p className="font-semibold text-sm text-slate-800 truncate">
                              {item.productName}
                            </p>

                            <p className="text-xs text-slate-500">
                              {item.variantName}
                            </p>

                            {order.moreItemsCount > 0 && (
                              <p className="text-[11px] font-semibold text-amber-900">
                                +{order.moreItemsCount} more item
                                {order.moreItemsCount > 1 ? "s" : ""}
                              </p>
                            )}

                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex rounded bg-[#A8D97B]/20 px-1.5 py-0.5 text-[10px] font-medium text-[#2F5E2E]">
                                Qty: {item.quantity}
                              </span>
                              <span className="text-[10px] text-slate-400">
                                • Purchased
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer & Order Level Review */}
                  <div className="flex items-center justify-between border-t border-slate-100 pt-3 flex-wrap gap-2">
                    {isDelivered && (
                      <div className="flex items-center gap-2">
                        {existingReview ? (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={13}
                                  className={
                                    star <= existingReview.rating
                                      ? "fill-amber-400 text-amber-400"
                                      : "text-slate-300"
                                  }
                                />
                              ))}
                            </div>
                            <span className="text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                              Order Rated
                            </span>
                            <button
                              onClick={(e) => handleOpenOrderReview(e, order, "view")}
                              className="text-[11px] text-[#2F5E2E] underline font-semibold hover:text-[#6DBE45]"
                            >
                              View
                            </button>
                            <button
                              onClick={(e) => handleOpenOrderReview(e, order, "edit")}
                              className="text-[11px] text-amber-700 underline font-semibold hover:text-amber-800"
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => handleOpenOrderReview(e, order, "create")}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                          >
                            <Star size={13} className="text-amber-500 fill-amber-400" />
                            Rate Order Experience
                          </button>
                        )}
                      </div>
                    )}

                    {/* <div className="ml-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#2F5E2E] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:bg-amber-950 hover:shadow-md active:scale-[0.98]"
                      >
                        <RotateCcw size={14} />
                        <span>Reorder</span>
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      <ReviewModal
        open={isReviewModalOpen}
        mode={reviewMode}
        product={
          selectedReviewOrder
            ? {
                productName: `Order #${selectedReviewOrder.orderNumber}`,
                variantName: "Order Delivery Experience",
                image: selectedReviewOrder.thumbnailImage || ImagePath.NoImage,
              }
            : undefined
        }
        review={
          selectedReviewOrder
            ? orderReviews[selectedReviewOrder.orderId]
            : undefined
        }
        onClose={() => {
          setIsReviewModalOpen(false);
          setSelectedReviewOrder(null);
          setReviewMode(null);
        }}
        onSubmit={handleSubmitOrderReview}
        onRatingChange={handleRatingChange}
      />
    </div>
  );
};

export default CustomerOrdersScreen;